import React from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * Returns extra style controls for the right-side style panel.
 */
export const getSlidingTextExtraStyleControls = ({ content, onChange }) => ({
  colorsFontsExtras: (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Marquee Colors</h4>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Text Color</label>
        <div className="flex items-center gap-2">
          <input type="color" value={content.textColor || '#ffffff'} onChange={(e) => onChange({ textColor: e.target.value })}
            className="w-10 h-10 rounded cursor-pointer border border-gray-700 flex-shrink-0" />
          <input type="text" value={content.textColor || '#ffffff'} onChange={(e) => onChange({ textColor: e.target.value })}
            className="flex-1 px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white text-sm focus:border-[#749555] focus:outline-none" />
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Background Color</label>
        <div className="flex items-center gap-2">
          <input type="color" value={content.backgroundColor || '#749555'} onChange={(e) => onChange({ backgroundColor: e.target.value })}
            className="w-10 h-10 rounded cursor-pointer border border-gray-700 flex-shrink-0" />
          <input type="text" value={content.backgroundColor || '#749555'} onChange={(e) => onChange({ backgroundColor: e.target.value })}
            className="flex-1 px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white text-sm focus:border-[#749555] focus:outline-none" />
        </div>
      </div>
    </div>
  ),
});

/**
 * SlidingTextEditor - Editor for sliding text marquee block
 */
const SlidingTextEditor = ({ block, onChange, theme, hideStylePanels }) => {
  const { content } = block;
  const items = content.items || [];

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const handleItemChange = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    handleChange('items', newItems);
  };

  const addItem = () => {
    handleChange('items', [...items, 'New text']);
  };

  const removeItem = (index) => {
    if (items.length <= 1) return;
    const newItems = items.filter((_, i) => i !== index);
    handleChange('items', newItems);
  };

  const extras = getSlidingTextExtraStyleControls({ content, onChange });

  return (
    <div className="space-y-4">
      {/* Text Items */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Text Items
        </label>
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <GripVertical size={16} className="text-gray-600 flex-shrink-0" />
              <input
                type="text"
                value={item}
                onChange={(e) => handleItemChange(index, e.target.value)}
                placeholder="Enter text..."
                className="flex-1 px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
              />
              <button
                onClick={() => removeItem(index)}
                disabled={items.length <= 1}
                className="p-2 text-gray-400 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Remove item"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addItem}
          className="mt-2 w-full py-2 border border-dashed border-gray-600 hover:border-[#749555] rounded-lg text-gray-400 hover:text-[#749555] transition-colors flex items-center justify-center gap-2"
        >
          <Plus size={16} />
          Add Text
        </button>
      </div>

      {/* Separator */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Separator
        </label>
        <input
          type="text"
          value={content.separator || '•'}
          onChange={(e) => handleChange('separator', e.target.value)}
          placeholder="•"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
        <p className="text-xs text-gray-500 mt-1">Character between each text item</p>
      </div>

      {/* Speed */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Scroll Speed
        </label>
        <div className="flex gap-2">
          {[
            { value: 'slow', label: 'Slow' },
            { value: 'normal', label: 'Normal' },
            { value: 'fast', label: 'Fast' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleChange('speed', option.value)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                content.speed === option.value
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Preview
        </label>
        <div
          className="overflow-hidden rounded-lg py-3"
          style={{ backgroundColor: content.backgroundColor || '#749555' }}
        >
          <div
            className="whitespace-nowrap animate-marquee"
            style={{ color: content.textColor || '#ffffff' }}
          >
            {items.map((item, i) => (
              <span key={i} className="mx-4 font-medium">
                {item}
                {i < items.length - 1 && (
                  <span className="mx-4 opacity-60">{content.separator || '•'}</span>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Style Panel */}
      {!hideStylePanels && (
        <ColorsFontsPanel style={content.style || {}} onChange={handleStyleChange} features={BLOCK_FEATURES['sliding-text']} theme={theme || {}} blockType="sliding-text" extraControls={extras.colorsFontsExtras} />
      )}
    </div>
  );
};

export default SlidingTextEditor;
